/* ============================================================================
   GlowingOrb internals - palettes, geometry, emotion patterns, WebGL rim
   ----------------------------------------------------------------------------
   Pure helpers and shader source for hollie's orb, split out of GlowingOrb.tsx
   so the maths is unit-testable (jsdom has no canvas, so the rendering layers
   stay null-safe). Ported faithfully from the brand guide's reference orb
   (marketing-website/src/lib/glowing-orb.ts), the house reference for hollie's
   look and motion (docs/DESIGN.md, section 06). If you change a ported piece
   here, mirror it in that file so the brand guide and the product never
   silently diverge.

   The platform difference from the brand guide: "speaking" is driven by a real
   microphone AnalyserNode (see GlowingOrb.tsx), not the brand guide's synthetic
   speech envelope, so BIN_COUNT / VOLUME_CEILING live here too.
   ========================================================================== */

/**
 * Visual state of the orb. Mutually exclusive.
 * - `listening`  : session is live, mic open, awaiting user speech.
 * - `speaking`   : AI is talking; orb reacts to the analyser volume.
 * - `connecting` : initial bring-up, reconnect, or processing.
 */
export type OrbState = 'listening' | 'speaking' | 'connecting';

/** Whole merged circle, or the field of dots. */
export type OrbShape = 'orb' | 'dots';

/** The emotion pattern animated across the dot field while split. */
export type OrbPattern = 'breathe' | 'pulse' | 'wave' | 'ripple' | 'sparkle' | 'spin' | 'heart' | 'eyes';

/** A declarative form: the merged circle, or a dot-field emotion pattern. */
export type OrbForm = 'orb' | OrbPattern;

/** Red/green/blue triplet, channels 0..255. */
export type RGB = [number, number, number];

/**
 * Palette for one orb mood. `base` is the liquid canvas tint; `stop0-2` and
 * `bloom` drive the voice bloom and shadow; `ring0-3` feed the SVG fallback rim
 * gradient. The WebGL rim carries its own fixed churn colours.
 */
export interface Palette {
  base: RGB;
  stop0: RGB;
  stop1: RGB;
  stop2: RGB;
  bloom: RGB;
  ring0: RGB;
  ring1: RGB;
  ring2: RGB;
  ring3: RGB;
}

/* ---- Tunables (verbatim from the brand guide orb) ---- */
/** Lerp factor per frame for amplitude: lower is smoother, higher is snappier. */
export const SMOOTHING = 0.12;
/** Cross-state tween duration, ms. Drives colour wash + ring ripple in/out. */
export const STATE_TWEEN_MS = 900;
/** How many frequency bins to average for the volume estimate (real mic). */
export const BIN_COUNT = 50;
/**
 * Divisor that normalises summed byte-frequency into a 0..1 amplitude. Kept high
 * enough that loud AI speech does NOT clip to 1.0: clipping pins the swell at its
 * maximum and kills the pulse. Headroom lets the voice envelope rise and fall.
 */
export const VOLUME_CEILING = 200;
/**
 * Ceiling on the speaking amplitude (platform real-mic path only). The brand
 * guide's synthetic envelope peaks around 0.66; the redesigned rim hugs the core
 * (band at viewBox 54 vs a core that swells past 50), so we cap a touch below
 * that. This guarantees the core can never swell into the rim no matter how hot
 * the playback runs, while the pulse still plays out below the cap.
 */
export const SPEAK_PEAK = 0.62;

/** Horizontal float drift amplitude, % of container. Small so it reads as a bob. */
export const FLOAT_AMOUNT_X = 1.5;
/** Vertical float drift amplitude, % of container. Dominant axis. */
export const FLOAT_AMOUNT_Y = 3.2;
export const FLOAT_FREQ_X = 0.55;
export const FLOAT_FREQ_Y = 0.42;

/* SVG fallback ring (the WebGL rim is primary): a lit rim at the GL band's
   radius. The core is 44% of the mount and swells to roughly 52 viewBox units
   at peak voice, staying clear of the rim at 54. */
export const RING_SEGMENTS = 96;
export const RING_BASE_RADIUS = 54;
export const RING_WAVE_HEIGHT = 5;
export const RING_WAVE_WIDTH = 0.22;
export const RING_WAVE_SPEED = 0.22;
export const RING_BOOST_PEAK = 3.5;
export const RING_STROKE_THIN = 3;
export const RING_STROKE_THICK = 4.5;
/** Wave amplitude leads the conn tween (x1.8, capped) so the bump shows moments
    after the switch instead of halfway through the 900ms ease. */
export const RING_WAVE_ATTACK = 1.8;
/** Shape morphs ride the rim wave at a fraction of the connecting wave: a
    passing shimmer, not the processing sprint. */
export const SHAPE_WAVE_GAIN = 0.3;
export const RING_BLOOM_WIDTH_MUL = 3.5;
export const RING_BLOOM_ALPHA = 0.55;

export const INNER_WAVE_WIDTH = 0.22;
export const INNER_WAVE_AMPLITUDE = 0.01;
export const INNER_WAVE_BASE_RADIUS = 0.49;
export const INNER_WAVE_SEGMENTS = 72;
export const INNER_WAVE_SPEED = 0.35;

export const MOTION_GAIN_SMALL = 1.5;
export const MOTION_GAIN_LARGE = 1.0;
export const MOTION_SIZE_MIN = 180;
export const MOTION_SIZE_MAX = 400;

/* ---- Dot-matrix shape morph ----
   The core's liquid paint is masked by concentric rings of circles (a polar
   grid, so the silhouette is a circle, never a hexagon). Fully merged the mask
   circles are just big enough to union into one solid disc; fully split they
   shrink to individual dots whose radii follow an emotion pattern. The merge is
   every dot swelling at once until neighbours touch and fuse, not a radial
   reveal, so DOT_MERGE_RADIUS is kept tight. Units are core-fractions (0..1). */
export const SHAPE_TWEEN_MS = 1100;
export const DOT_RINGS = 6;
export const DOT_GRID_MAX_DIST = 0.44;
export const DOT_MERGE_RADIUS = 0.082;
export const DOT_BASE_RADIUS = 0.026;
export const PATTERN_TWEEN_MS = 700;

/* ---- Palettes (matched to the Volt brand tokens, docs/DESIGN.md 06) ----
   The interior liquid lives in its own deep palette (see GlowingOrb.tsx
   mountLiquidCore call). These palettes drive the bloom, shadow and SVG
   fallback rim, not the liquid itself. */
/** Ready/idle palette: lavender base, deep Volt currents. */
export const PALETTE_READY: Palette = {
  base: [168, 143, 255], stop0: [168, 143, 255], stop1: [168, 143, 255], stop2: [110, 63, 255],
  bloom: [89, 41, 232], ring0: [240, 246, 255], ring1: [188, 206, 255], ring2: [168, 143, 255], ring3: [110, 63, 255],
};
/** Speaking palette: a touch brighter, voice pulls the bloom up. */
export const PALETTE_SPEAKING: Palette = {
  base: [168, 143, 255], stop0: [139, 102, 255], stop1: [139, 102, 255], stop2: [110, 63, 255],
  bloom: [139, 102, 255], ring0: [244, 250, 255], ring1: [196, 214, 255], ring2: [139, 102, 255], ring3: [89, 41, 232],
};
/** Connecting palette: cold, pale base with a deep Volt tide washing through. */
export const PALETTE_CONNECTING: Palette = {
  base: [226, 236, 250], stop0: [210, 222, 245], stop1: [168, 143, 255], stop2: [110, 63, 255],
  bloom: [168, 143, 255], ring0: [247, 250, 252], ring1: [222, 228, 255], ring2: [236, 242, 250], ring3: [210, 222, 245],
};

/* ---- Helpers ---- */

/** Cubic ease-in-out: flat slope at 0 and 1, peak velocity at 0.5. */
export function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/** Linear interpolation. */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Per-channel RGB interpolation. */
export function lerpRGB(a: RGB, b: RGB, t: number): RGB {
  return [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
}

/** Clamp to the unit interval. */
export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Hermite smoothstep between edges `a` and `b`, clamped to [0,1]. */
export function smoothstep(a: number, b: number, v: number): number {
  const x = clamp01((v - a) / (b - a));
  return x * x * (3 - 2 * x);
}

/**
 * Raised-cosine pulse around a circle. `u` and `phase` are in [0,1]; returns a
 * value in [0,1] peaked at `phase`, falling to 0 at +/-width (distance measured
 * as the shorter arc, so the pulse wraps seamlessly).
 */
export function travelingPulse(u: number, phase: number, width: number): number {
  let d = Math.abs(u - phase);
  if (d > 0.5) d = 1 - d;
  if (d > width) return 0;
  return 0.5 * (1 + Math.cos((d / width) * Math.PI));
}

/**
 * Clip-path string (objectBoundingBox units, 0..1) for the orb's inner edge:
 * the same travelling raised-cosine pulse as the ring but much smaller. Radius
 * stays under 0.5 so the bumps do not overshoot the bounding box.
 */
export function buildInnerClipPath(phase: number): string {
  let p = '';
  for (let i = 0; i < INNER_WAVE_SEGMENTS; i++) {
    const u = i / INNER_WAVE_SEGMENTS;
    const th = u * Math.PI * 2;
    const r = INNER_WAVE_BASE_RADIUS + travelingPulse(u, phase, INNER_WAVE_WIDTH) * INNER_WAVE_AMPLITUDE;
    const x = 0.5 + r * Math.cos(th);
    const y = 0.5 + r * Math.sin(th);
    p += (i === 0 ? 'M' : 'L') + x.toFixed(4) + ' ' + y.toFixed(4) + ' ';
  }
  return p + 'Z';
}

/**
 * SVG path for the wavy ring at the given phase and amplitude (viewBox 0..200,
 * centre 100,100). `scale` multiplies the radius about the centre.
 */
export function buildRingPath(phase: number, amplitude: number, scale = 1): string {
  const cx = 100, cy = 100;
  let p = '';
  for (let i = 0; i < RING_SEGMENTS; i++) {
    const u = i / RING_SEGMENTS;
    const th = u * Math.PI * 2;
    const r = (RING_BASE_RADIUS + travelingPulse(u, phase, RING_WAVE_WIDTH) * RING_WAVE_HEIGHT * amplitude) * scale;
    const x = cx + r * Math.cos(th);
    const y = cy + r * Math.sin(th);
    p += (i === 0 ? 'M' : 'L') + x.toFixed(2) + ' ' + y.toFixed(2) + ' ';
  }
  return p + 'Z';
}

/* ---- Dot matrix ---- */

/** One masking dot: position (0..1), normalised ring distance, angle, seed. */
export interface Dot {
  x: number;
  y: number;
  dist: number;
  angle: number;
  seed: number;
}

/**
 * Build the polar dot grid: a centre dot plus concentric rings. Each ring holds
 * roughly 2*PI*ring dots so the arc gap matches the radial gap, and alternate
 * rings are rotated half a step so dots do not line up into spokes. Each dot
 * gets a deterministic seed so sparkle phases never sync.
 */
export function buildDotGrid(): Dot[] {
  const dots: Dot[] = [{ x: 0.5, y: 0.5, dist: 0, angle: 0, seed: 0.5 }];
  for (let ring = 1; ring <= DOT_RINGS; ring++) {
    const r = (ring / DOT_RINGS) * DOT_GRID_MAX_DIST;
    const count = Math.round(Math.PI * 2 * ring);
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2 + (ring % 2) * (Math.PI / count);
      const seed = Math.abs((Math.sin((ring * 73 + i) * 127.1) * 43758.5453) % 1);
      dots.push({ x: 0.5 + r * Math.cos(angle), y: 0.5 + r * Math.sin(angle), dist: ring / DOT_RINGS, angle, seed });
    }
  }
  return dots;
}

/** The shared dot grid, built once. */
export const DOT_GRID: Dot[] = buildDotGrid();

/**
 * Emotion field: per-dot intensity 0..1 that modulates the split-dot radius.
 * Each pattern is the brand guide's reference shape (docs/DESIGN.md, "Her
 * shapes").
 */
export function patternField(pattern: OrbPattern, dot: Dot, t: number): number {
  switch (pattern) {
    case 'breathe':
      return 0.62 + 0.38 * Math.sin(t * 1.5 - dot.dist * 2.4);
    case 'pulse': {
      const beat = Math.pow(Math.max(0, Math.sin(t * 2.4 - dot.dist * 3.2)), 3);
      return 0.40 + 0.60 * beat;
    }
    case 'wave':
      return 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(t * 2.2 - (dot.x + dot.y) * 9));
    case 'ripple': {
      const u = (t * 0.30) % 1;
      return 0.38 + 0.62 * travelingPulse(dot.dist * 0.5, u, 0.18);
    }
    case 'sparkle':
      return 0.30 + 0.70 * (0.5 + 0.5 * Math.sin(t * 2.6 + dot.seed * 12.9) * Math.sin(t * 1.7 + dot.seed * 31.7));
    case 'spin': {
      const lobe = 0.5 + 0.5 * Math.cos(dot.angle - t * 1.8);
      return 0.34 + 0.66 * Math.pow(lobe, 2.2);
    }
    case 'heart': {
      // Lub-dub: two gaussian thumps per 1.4s beat. The heart is the icon
      // construction (rotated diamond + two circle caps) as a signed distance
      // field, so its cusp and tip stay crisp on a coarse dot grid. The whole
      // heart swells and brightens with each thump, and every beat sends a soft
      // pulse ring radiating out through the dim dots: the beat travels through
      // her body.
      const u = (t % 1.4) / 1.4;
      const thump = Math.exp(-Math.pow((u - 0.10) / 0.05, 2)) + 0.65 * Math.exp(-Math.pow((u - 0.30) / 0.06, 2));
      const s = 0.21 * (1 + 0.13 * Math.min(1, thump));
      const hx = (dot.x - 0.5) / s, hy = (0.47 - dot.y) / s;
      const dDiamond = (Math.abs(hx) + Math.abs(hy) - 1) * Math.SQRT1_2;
      const dCapL = Math.hypot(hx + 0.5, hy - 0.5) - Math.SQRT1_2;
      const dCapR = Math.hypot(hx - 0.5, hy - 0.5) - Math.SQRT1_2;
      const sd = Math.min(dDiamond, dCapL, dCapR) * s;
      let v = (0.66 + 0.34 * Math.min(1, thump)) * (1 - smoothstep(-0.012, 0.018, sd));
      const p = (u - 0.10) / 0.7; // pulse ring launched by the lub, fading as it travels
      if (p > 0 && p < 1) v = Math.max(v, Math.exp(-Math.pow((dot.dist - p) / 0.12, 2)) * 0.38 * (1 - p));
      return 0.10 + 0.90 * v;
    }
    default: {
      // 'eyes': Two pupils that wander left and right (tanh dwells at the
      // extremes, like a held gaze), squashing flat for a blink every 3.6s.
      const cycle = t % 3.6;
      const blink = cycle < 0.32 ? Math.sin((cycle / 0.32) * Math.PI) : 0;
      const gaze = Math.tanh(Math.sin(t * 0.55) * 2.2) * 0.06;
      const rx = 0.105, ry = Math.max(0.02, 0.105 * (1 - 0.88 * blink));
      const dy = dot.y - 0.46;
      let v = 0;
      for (const sx of [-0.14, 0.14]) {
        const dx = dot.x - (0.5 + sx + gaze);
        v = Math.max(v, 1 - smoothstep(0.8, 1.2, Math.hypot(dx / rx, dy / ry)));
      }
      return 0.12 + 0.88 * v;
    }
  }
}

/**
 * Per-dot cross-fade state between patterns. `last` is updated every frame,
 * `from` is a snapshot taken when the pattern changed, `blend` eases 0->1, so
 * any pattern switch is fluid, never a snap.
 */
export interface DotFieldState {
  last: Float64Array;
  from: Float64Array | null;
  blend: number;
}

/**
 * Mask the painted liquid down to the dot rings. At morph near 0 every circle is
 * DOT_MERGE_RADIUS so the union is one solid disc (visually the unmasked orb);
 * every dot grows or shrinks in unison, so a merge reads as the dots swelling
 * into one another, never a radial reveal. The split field keeps the orb's
 * liquid nature: each dot drifts on its own sum-of-sines orbit and the whole
 * field sways, both clocked by `flow` and scaled by `morph` so dots return to
 * their exact grid as they fuse.
 */
export function applyDotMask(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  morph: number,
  pattern: OrbPattern,
  t: number,
  amp: number,
  flow: number,
  fs: DotFieldState,
): void {
  const s = Math.min(w, h);
  const sway = Math.sin(flow * 0.40) * 0.020 + Math.sin(flow * 0.23 + 1.7) * 0.014; // radians
  const cosS = Math.cos(sway), sinS = Math.sin(sway);
  const wobAmp = 0.012 * morph * (1 + amp * 0.8); // voice makes the dots dance harder
  ctx.save();
  ctx.globalCompositeOperation = 'destination-in';
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  for (let i = 0; i < DOT_GRID.length; i++) {
    const dot = DOT_GRID[i];
    let field = patternField(pattern, dot, t);
    if (fs.from && fs.blend < 1) field = fs.from[i] + (field - fs.from[i]) * fs.blend;
    fs.last[i] = field;
    const splitR = DOT_BASE_RADIUS * (0.5 + 0.7 * field) * (1 + amp * 0.35);
    const r = lerp(DOT_MERGE_RADIUS, splitR, morph) * s;
    const wx = (Math.sin(flow * (0.50 + dot.seed * 0.40) + dot.seed * 6.28) * 0.7 + Math.sin(flow * 0.31 + dot.seed * 12.6) * 0.3) * wobAmp;
    const wy = (Math.sin(flow * (0.42 + dot.seed * 0.50) + dot.seed * 9.42) * 0.7 + Math.sin(flow * 0.27 + dot.seed * 17.3) * 0.3) * wobAmp;
    const ox = dot.x - 0.5, oy = dot.y - 0.5;
    const x = (0.5 + ox * cosS - oy * sinS + wx) * w;
    const y = (0.5 + oy * cosS + ox * sinS + wy) * h;
    ctx.moveTo(x + r, y);
    ctx.arc(x, y, r, 0, Math.PI * 2);
  }
  ctx.fill();
  ctx.restore();
}

/* ============================================================================
   WebGL rim (ported from the brand guide's ChurnOrb ring)
   ----------------------------------------------------------------------------
   The ring is a luminous band drawn in a fragment shader: simplex noise wobbles
   the band's radius, two colours churn around it, a bright light orbits it, and
   the cursor ripples it. Colour and glow are the churn demo's settings verbatim:
   fixed base colours (vivid purple / cyan / deep ink-blue) hue-rotated by uHue.
   The rim keeps its own colour and does NOT follow her state palette; only the
   band radius, the connecting wave, and the recomposited dark vignette are
   brand-guide adaptations. If WebGL is unavailable the SVG ring is the fallback.
   ========================================================================== */

/** Vertex shader: a single fullscreen triangle. */
export const RING_GL_VERT =
  'precision highp float;attribute vec2 position;attribute vec2 uv;varying vec2 vUv;' +
  'void main(){vUv=uv;gl_Position=vec4(position,0.0,1.0);}';

/** Fragment shader: the churn band with noise, orbiting light, wave, and ripple. */
export const RING_GL_FRAG = `
precision highp float;
uniform float iTime;
uniform vec3 iResolution;
uniform float uHue;
uniform float uRot;
uniform float uWave;
uniform float uWavePhase;
uniform vec2 uPointer;
uniform float uProximity;
varying vec2 vUv;

vec3 rgb2yiq(vec3 c){
  float y = dot(c, vec3(0.299, 0.587, 0.114));
  float i = dot(c, vec3(0.596, -0.274, -0.322));
  float q = dot(c, vec3(0.211, -0.523, 0.312));
  return vec3(y, i, q);
}

vec3 yiq2rgb(vec3 c){
  float r = c.x + 0.956 * c.y + 0.621 * c.z;
  float g = c.x - 0.272 * c.y - 0.647 * c.z;
  float b = c.x - 1.106 * c.y + 1.703 * c.z;
  return vec3(r, g, b);
}

vec3 adjustHue(vec3 color, float hueDeg){
  float hueRad = hueDeg * 3.14159265 / 180.0;
  vec3 yiq = rgb2yiq(color);
  float cosA = cos(hueRad);
  float sinA = sin(hueRad);
  float i = yiq.y * cosA - yiq.z * sinA;
  float q = yiq.y * sinA + yiq.z * cosA;
  yiq.y = i;
  yiq.z = q;
  return yiq2rgb(yiq);
}

vec3 hash33(vec3 p3){
  p3 = fract(p3 * vec3(0.1031, 0.11369, 0.13787));
  p3 += dot(p3, p3.yxz + 19.19);
  return -1.0 + 2.0 * fract(vec3(p3.x + p3.y, p3.x + p3.z, p3.y + p3.z) * p3.zyx);
}

float snoise3(vec3 p){
  const float K1 = 0.333333333;
  const float K2 = 0.166666667;
  vec3 i = floor(p + (p.x + p.y + p.z) * K1);
  vec3 d0 = p - (i - (i.x + i.y + i.z) * K2);
  vec3 e = step(vec3(0.0), d0 - d0.yzx);
  vec3 i1 = e * (1.0 - e.zxy);
  vec3 i2 = 1.0 - e.zxy * (1.0 - e);
  vec3 d1 = d0 - (i1 - K2);
  vec3 d2 = d0 - (i2 - K1);
  vec3 d3 = d0 - 0.5;
  vec4 h = max(0.6 - vec4(dot(d0, d0), dot(d1, d1), dot(d2, d2), dot(d3, d3)), 0.0);
  vec4 n = h * h * h * h * vec4(dot(d0, hash33(i)), dot(d1, hash33(i + i1)), dot(d2, hash33(i + i2)), dot(d3, hash33(i + 1.0)));
  return dot(vec4(31.316), n);
}

vec4 extractAlpha(vec3 colorIn){
  float a = max(max(colorIn.r, colorIn.g), colorIn.b);
  return vec4(colorIn.rgb / (a + 1e-5), a);
}

float light1(float intensity, float attenuation, float dist){ return intensity / (1.0 + dist * attenuation); }
float light2(float intensity, float attenuation, float dist){ return intensity / (1.0 + dist * dist * attenuation); }

const vec3 baseColor1 = vec3(0.611765, 0.262745, 0.996078);
const vec3 baseColor2 = vec3(0.298039, 0.760784, 0.913725);
const vec3 baseColor3 = vec3(0.062745, 0.078431, 0.600000);
const float innerRadius = 0.42;
const float noiseScale = 0.65;
const float vigAlpha = 0.78;
const vec3 inkColor = vec3(0.016, 0.020, 0.120);

vec4 draw(vec2 uv){
  vec3 color1 = adjustHue(baseColor1, uHue);
  vec3 color2 = adjustHue(baseColor2, uHue);
  vec3 color3 = adjustHue(baseColor3, uHue);

  float ang = atan(uv.y, uv.x);
  float len = length(uv);
  float invLen = len > 0.0 ? 1.0 / len : 0.0;

  float n0 = snoise3(vec3(uv * noiseScale, iTime * 0.5)) * 0.5 + 0.5;
  float r0 = mix(0.54, 0.63, n0);
  float ringU = ang * 0.15915494 + 0.5;
  float du = abs(fract(ringU + uWavePhase + 0.5) - 0.5);
  float wavePulse = du < 0.22 ? 0.5 * (1.0 + cos(du * 14.2799666)) : 0.0;
  r0 += wavePulse * uWave * 0.05;
  float d0 = distance(uv, (r0 * invLen) * uv);
  float v0 = light1(1.4, 4.0, d0);
  v0 *= smoothstep(r0 * 1.12, r0, len);
  float cl = cos(ang + iTime * 2.0) * 0.5 + 0.5;

  float a = iTime * -1.0;
  vec2 pos = vec2(cos(a), sin(a)) * r0;
  float d = distance(uv, pos);
  float v1 = light2(1.5, 5.0, d);
  v1 *= light1(1.0, 50.0, d0);

  float v2 = smoothstep(1.0, mix(innerRadius, 1.0, n0 * 0.5), len);
  float v3 = smoothstep(innerRadius, mix(innerRadius, 1.0, 0.5), len);

  vec3 col = mix(color1, color2, cl);
  col = mix(color3, col, v0);
  col = (col + v1) * v2 * v3;
  col = clamp(col, 0.0, 1.0);
  vec4 res = extractAlpha(col);
  float body = v2 * v3 * vigAlpha * (1.0 - smoothstep(r0 - 0.02, r0 + 0.08, len));
  body *= 1.0 - v0 * 0.85;
  float outA = res.a + body * (1.0 - res.a);
  vec3 outRgb = (res.rgb * res.a + inkColor * body * (1.0 - res.a)) / max(outA, 1e-4);
  return vec4(outRgb, outA);
}

void main(){
  vec2 fragCoord = vUv * iResolution.xy;
  vec2 center = iResolution.xy * 0.5;
  float size = min(iResolution.x, iResolution.y);
  vec2 uv = (fragCoord - center) / size * 2.0;

  float s = sin(uRot);
  float c = cos(uRot);
  uv = vec2(c * uv.x - s * uv.y, s * uv.x + c * uv.y);

  vec4 color = draw(uv);

  vec2 mouseUv = (uPointer - center) / size * 2.0;
  mouseUv = vec2(c * mouseUv.x - s * mouseUv.y, s * mouseUv.x + c * mouseUv.y);
  float pointerDistance = distance(uv, mouseUv);
  float rippleBand = sin(pointerDistance * 16.0 - iTime * 3.8) * 0.5 + 0.5;
  float rippleFalloff = smoothstep(1.02, 0.08, pointerDistance);
  float orbMask = smoothstep(1.03, 0.82, length(uv));
  float softBand = smoothstep(0.26, 0.92, rippleBand);
  float ripple = softBand * rippleFalloff * orbMask * uProximity;
  vec3 rippleColor = mix(baseColor2, baseColor1, 0.38);
  color.rgb += rippleColor * ripple * 0.16;
  color.a = max(color.a, ripple * 0.14);

  gl_FragColor = vec4(color.rgb * color.a, color.a);
}
`;

/** A compiled WebGL rim program with its uniform locations. */
export interface RingGL {
  gl: WebGLRenderingContext;
  u: Record<string, WebGLUniformLocation | null>;
}

/**
 * Compile and link the rim shader on `canvas`. Returns null when WebGL is
 * unavailable (server, jsdom, or a context-loss), so the caller falls back to
 * the SVG rim.
 */
export function initRingGL(canvas: HTMLCanvasElement): RingGL | null {
  const gl = canvas.getContext('webgl', { alpha: true, antialias: true, premultipliedAlpha: false });
  if (!gl) return null;
  const compile = (type: number, src: string): WebGLShader | null => {
    const sh = gl.createShader(type);
    if (!sh) return null;
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) { gl.deleteShader(sh); return null; }
    return sh;
  };
  const vs = compile(gl.VERTEX_SHADER, RING_GL_VERT), fs = compile(gl.FRAGMENT_SHADER, RING_GL_FRAG);
  const prog = gl.createProgram();
  if (!vs || !fs || !prog) {
    if (vs) gl.deleteShader(vs);
    if (fs) gl.deleteShader(fs);
    if (prog) gl.deleteProgram(prog);
    return null;
  }
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    gl.deleteProgram(prog);
    return null;
  }
  // The linked program retains the compiled result, so detach and delete the
  // shader objects (the driver ref-counts them by attachment, they leak otherwise).
  gl.detachShader(prog, vs);
  gl.deleteShader(vs);
  gl.detachShader(prog, fs);
  gl.deleteShader(fs);
  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 0, 0, 3, -1, 2, 0, -1, 3, 0, 2]), gl.STATIC_DRAW);
  gl.useProgram(prog);
  const posL = gl.getAttribLocation(prog, 'position'), uvL = gl.getAttribLocation(prog, 'uv');
  gl.enableVertexAttribArray(posL);
  gl.vertexAttribPointer(posL, 2, gl.FLOAT, false, 16, 0);
  gl.enableVertexAttribArray(uvL);
  gl.vertexAttribPointer(uvL, 2, gl.FLOAT, false, 16, 8);
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  gl.clearColor(0, 0, 0, 0);
  const u: Record<string, WebGLUniformLocation | null> = {};
  for (const n of ['iTime', 'iResolution', 'uHue', 'uRot', 'uWave', 'uWavePhase', 'uPointer', 'uProximity']) {
    u[n] = gl.getUniformLocation(prog, n);
  }
  return { gl, u };
}
